import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  ArrowLeft,
  Wrench,
  Briefcase,
  Sparkles,
  Phone,
  Mail,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { api, BUSINESS } from "@/lib/api";

const TRADES = [
  "TV Mounting",
  "General Mounting",
  "Furniture Assembly",
  "Moving Help",
  "Junk Removal",
  "Painting",
  "Drywall",
  "Carpentry",
  "Electrical",
  "Plumbing",
  "General Repairs",
];

const AVAILABILITY = ["Weekdays", "Weekends", "Evenings", "On-call"];

const PERKS = [
  { icon: Briefcase, label: "Steady, flexible jobs in your area" },
  { icon: Wrench, label: "We handle the booking, you focus on the work" },
  { icon: Sparkles, label: "Fast pay & repeat customer pipeline" },
];

export default function JoinTeamPage() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    trades: [],
    years_experience: "",
    has_tools: "",
    vehicle: "",
    availability: [],
    insured: "",
    why_join: "",
  });
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArray = (k, value) =>
    setForm((f) => {
      const set = new Set(f[k]);
      if (set.has(value)) set.delete(value);
      else set.add(value);
      return { ...f, [k]: Array.from(set) };
    });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.city) {
      toast.error("Please complete name, phone, email and city.");
      return;
    }
    if (form.trades.length === 0) {
      toast.error("Pick at least one trade you offer.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contractor-applications", {
        ...form,
        years_experience: form.years_experience
          ? Number(form.years_experience)
          : null,
      });
      setSuccess(true);
      toast.success("Application received! We'll be in touch shortly.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit application.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-20">
        <Link
          to="/"
          data-testid="join-back"
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-8"
        >
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* LEFT — pitch */}
          <div className="lg:col-span-5">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">
              Careers
            </p>
            <h1
              data-testid="join-headline"
              className="pf-heading mt-3 text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900"
            >
              Join The Phoenix Fix team.
            </h1>
            <p className="mt-5 text-slate-600 leading-relaxed">
              We’re building a network of reliable, skilled home-service pros
              across metro Atlanta. If you take pride in your work, show up on
              time, and respect the customer’s home — we want to hear from you.
            </p>

            <div className="mt-10 space-y-5">
              {PERKS.map((p) => {
                const Icon = p.icon;
                return (
                  <div key={p.label} className="flex items-start gap-3">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
                      <Icon className="h-5 w-5" strokeWidth={1.75} />
                    </span>
                    <p className="text-sm text-slate-700 mt-1.5">{p.label}</p>
                  </div>
                );
              })}
            </div>

            <div className="mt-10 p-6 rounded-2xl bg-slate-900 text-white">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-400">
                Questions first?
              </p>
              <p className="mt-2 text-lg flex items-center gap-2">
                <Phone className="h-4 w-4" />
                <a href={BUSINESS.phoneHref}>{BUSINESS.phone}</a>
              </p>
              <p className="mt-1 text-sm text-slate-300 flex items-center gap-2">
                <Mail className="h-4 w-4" /> {BUSINESS.email}
              </p>
            </div>
          </div>

          {/* RIGHT — form */}
          <div className="lg:col-span-7">
            <div className="bg-white border border-slate-100 rounded-3xl p-8 md:p-10 shadow-sm">
              {success ? (
                <div data-testid="join-success" className="text-center py-10">
                  <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
                  <h2 className="pf-heading mt-5 text-2xl font-semibold text-slate-900">
                    Application received!
                  </h2>
                  <p className="mt-3 text-slate-600">
                    We’ll review your info and reach out shortly. Thanks for
                    your interest in joining The Phoenix Fix team.
                  </p>
                  <Link
                    to="/"
                    data-testid="join-success-home"
                    className="mt-8 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-blue-700 transition-colors"
                  >
                    Back to home
                  </Link>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-6" data-testid="join-form">
                  {/* Contact */}
                  <div>
                    <h2 className="pf-heading text-xl font-semibold text-slate-900">
                      About you
                    </h2>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <Label htmlFor="name">Full name *</Label>
                        <Input
                          id="name"
                          data-testid="join-name"
                          value={form.name}
                          onChange={(e) => update("name", e.target.value)}
                          placeholder="Jane Doe"
                          className="mt-2 h-11"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone *</Label>
                        <Input
                          id="phone"
                          data-testid="join-phone"
                          value={form.phone}
                          onChange={(e) => update("phone", e.target.value)}
                          placeholder="(404) 555-0123"
                          className="mt-2 h-11"
                          required
                        />
                      </div>
                    </div>
                    <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          data-testid="join-email"
                          value={form.email}
                          onChange={(e) => update("email", e.target.value)}
                          placeholder="you@example.com"
                          className="mt-2 h-11"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="city">City you’re based in *</Label>
                        <Input
                          id="city"
                          data-testid="join-city"
                          value={form.city}
                          onChange={(e) => update("city", e.target.value)}
                          placeholder="Marietta, GA"
                          className="mt-2 h-11"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Trades */}
                  <div className="border-t border-slate-100 pt-6">
                    <h2 className="pf-heading text-xl font-semibold text-slate-900">
                      What trades do you offer? *
                    </h2>
                    <p className="text-xs text-slate-500 mt-1">
                      Pick all that apply.
                    </p>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {TRADES.map((t) => (
                        <label
                          key={t}
                          className="flex items-center gap-3 rounded-xl border border-slate-100 px-4 py-3 hover:border-blue-200 cursor-pointer"
                          data-testid={`join-trade-${t}`}
                        >
                          <Checkbox
                            checked={form.trades.includes(t)}
                            onCheckedChange={() => toggleArray("trades", t)}
                          />
                          <span className="text-sm text-slate-800">{t}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Experience & logistics */}
                  <div className="border-t border-slate-100 pt-6">
                    <h2 className="pf-heading text-xl font-semibold text-slate-900">
                      Experience &amp; logistics
                    </h2>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <Label htmlFor="years">Years of experience</Label>
                        <Input
                          id="years"
                          type="number"
                          min={0}
                          max={60}
                          data-testid="join-years"
                          value={form.years_experience}
                          onChange={(e) =>
                            update("years_experience", e.target.value)
                          }
                          placeholder="3"
                          className="mt-2 h-11"
                        />
                      </div>
                      <div>
                        <Label>Do you have your own tools?</Label>
                        <Select
                          value={form.has_tools}
                          onValueChange={(v) => update("has_tools", v)}
                        >
                          <SelectTrigger className="mt-2 h-11" data-testid="join-tools">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Yes">Yes, a full set</SelectItem>
                            <SelectItem value="Some">Some tools</SelectItem>
                            <SelectItem value="No">No</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <Label>Vehicle</Label>
                        <Select
                          value={form.vehicle}
                          onValueChange={(v) => update("vehicle", v)}
                        >
                          <SelectTrigger className="mt-2 h-11" data-testid="join-vehicle">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Truck">Truck</SelectItem>
                            <SelectItem value="Van">Van</SelectItem>
                            <SelectItem value="SUV">SUV</SelectItem>
                            <SelectItem value="Car">Car</SelectItem>
                            <SelectItem value="None">No vehicle</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Insured / background check ready</Label>
                        <Select
                          value={form.insured}
                          onValueChange={(v) => update("insured", v)}
                        >
                          <SelectTrigger className="mt-2 h-11" data-testid="join-insured">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Yes">Yes</SelectItem>
                            <SelectItem value="In progress">In progress</SelectItem>
                            <SelectItem value="No">Not yet</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Availability */}
                  <div className="border-t border-slate-100 pt-6">
                    <h2 className="pf-heading text-xl font-semibold text-slate-900">
                      Availability
                    </h2>
                    <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {AVAILABILITY.map((a) => (
                        <label
                          key={a}
                          className="flex items-center gap-3 rounded-xl border border-slate-100 px-4 py-3 hover:border-blue-200 cursor-pointer"
                          data-testid={`join-avail-${a}`}
                        >
                          <Checkbox
                            checked={form.availability.includes(a)}
                            onCheckedChange={() => toggleArray("availability", a)}
                          />
                          <span className="text-sm text-slate-800">{a}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Why */}
                  <div className="border-t border-slate-100 pt-6">
                    <Label htmlFor="why">Why do you want to join?</Label>
                    <Textarea
                      id="why"
                      data-testid="join-why"
                      value={form.why_join}
                      onChange={(e) => update("why_join", e.target.value)}
                      placeholder="A short note on your experience, work style, or what kind of jobs you’re looking for."
                      rows={4}
                      className="mt-2"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={busy}
                    data-testid="join-submit"
                    className="w-full h-12 rounded-xl bg-slate-900 hover:bg-blue-700 text-white text-base font-semibold"
                  >
                    {busy ? "Submitting…" : "Submit Application"}
                  </Button>
                  <p className="text-xs text-slate-400 text-center">
                    We’ll only use your info to evaluate your application and reach out.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
